import { useEffect, useRef, useState } from "react";

import style from "./CourseCursor.module.css";
import Icon from "@/ui/icon/Icon";

const ARC_ID = "courseCursorArc";
const INK_ID = "courseCursorInk";

// Close in on the puck: with nothing behind the type, a wide ring reads as a
// button and some loose text rather than as one badge.
const RADIUS = 38;
// Pinning the text to the exact circumference makes the ring close on itself
// whatever the font does — no gap on a wide fallback face, no overlap on a
// narrow one.
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

// How far the badge closes the gap to the pointer each frame. Low enough to
// trail behind the hand, high enough not to feel like it is being dragged.
const EASING = 0.18;

// Long enough to cover the fade in CourseCursor.module.css. The badge keeps
// following the pointer for this long after leaving so it walks off with the
// hand; stopping the frame loop at the boundary strands it mid-panel.
const EXIT_MS = 260;

/**
 * A badge that replaces the pointer while it is over `target`: a play glyph
 * with the label turning around it.
 *
 * `target` is the element itself, not a ref object: the panel it tracks only
 * mounts once the hero video resolves, and a ref's identity never changes, so
 * an effect keyed on one would run against a null target and never re-arm.
 *
 * Only for a real pointer — a touch screen has no hover to follow, and would
 * leave the badge stranded wherever the last tap landed. The same media query
 * gates the `cursor: none` that hides the native arrow, so the two can never
 * disagree and leave a visitor with no pointer at all.
 */
function CourseCursor({ target, label }) {
	const badgeRef = useRef(null);
	const [isVisible, setIsVisible] = useState(false);

	useEffect(() => {
		if (!target) return;
		if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches)
			return;

		const pointer = { x: 0, y: 0 };
		const shown = { x: 0, y: 0 };
		let frame = null;
		let isOver = false;
		let followUntil = 0;

		// While the badge is on screen, and for the length of its fade after
		// that. Idling a frame loop on a page nobody is pointing at is waste.
		const shouldFollow = () => isOver || performance.now() < followUntil;

		const draw = () => {
			shown.x += (pointer.x - shown.x) * EASING;
			shown.y += (pointer.y - shown.y) * EASING;

			if (badgeRef.current) {
				badgeRef.current.style.transform = `translate3d(${shown.x}px, ${shown.y}px, 0) translate(-50%, -50%)`;
			}

			frame = shouldFollow() ? requestAnimationFrame(draw) : null;
		};

		const follow = () => {
			if (frame === null) frame = requestAnimationFrame(draw);
		};

		// On the window rather than the panel: once the pointer crosses out, the
		// panel stops reporting moves, and a badge that freezes while it fades
		// reads as something left behind rather than something leaving.
		const track = (event) => {
			pointer.x = event.clientX;
			pointer.y = event.clientY;
			if (shouldFollow()) follow();
		};

		const enter = (event) => {
			pointer.x = event.clientX;
			pointer.y = event.clientY;
			// Snap on entry. Easing in from the last exit point would send the
			// badge flying across the panel every time the pointer comes back.
			shown.x = pointer.x;
			shown.y = pointer.y;
			isOver = true;
			setIsVisible(true);
			follow();
		};

		const leave = () => {
			isOver = false;
			followUntil = performance.now() + EXIT_MS;
			setIsVisible(false);
			follow();
		};

		window.addEventListener("pointermove", track, { passive: true });
		target.addEventListener("pointerenter", enter);
		target.addEventListener("pointerleave", leave);

		return () => {
			if (frame !== null) cancelAnimationFrame(frame);
			window.removeEventListener("pointermove", track);
			target.removeEventListener("pointerenter", enter);
			target.removeEventListener("pointerleave", leave);
		};
	}, [target]);

	// One turn, not a repeated mantra: "Saznaj više o kursu" is a sentence, and
	// letting it take the whole circumference gives it the wide tracking of a
	// stamp rather than the cramped look of a phrase said twice.
	//
	// Non-breaking spaces around the separator: SVG collapses leading and
	// trailing whitespace, which closed the seam and left the bullet welded to
	// the S it wraps around to.
	const ring = `${label}\u00a0•\u00a0`.toUpperCase();

	return (
		<div
			ref={badgeRef}
			className={`${style.badge} ${isVisible ? style.visible : ""}`}
			aria-hidden="true"
		>
			<svg className={style.label} viewBox="0 0 100 100">
				<defs>
					<path
						id={ARC_ID}
						fill="none"
						d={`M 50,50 m -${RADIUS},0 a ${RADIUS},${RADIUS} 0 1,1 ${
							RADIUS * 2
						},0 a ${RADIUS},${RADIUS} 0 1,1 -${RADIUS * 2},0`}
					/>
					{/* The type sits straight on the footage, so these carry all
					    of its contrast: a tight contact shadow to lift the glyphs
					    off the picture, and a wide halo to hold them against a
					    busy frame. A single offset shadow reads as a sticker;
					    these read as ink. */}
					<filter
						id={INK_ID}
						x="-35%"
						y="-35%"
						width="170%"
						height="170%"
					>
						<feDropShadow
							dx="0"
							dy="0.5"
							stdDeviation="0.6"
							floodColor="#0b0916"
							floodOpacity="0.55"
						/>
						<feDropShadow
							dx="0"
							dy="0"
							stdDeviation="2.6"
							floodColor="#0b0916"
							floodOpacity="0.5"
						/>
					</filter>
				</defs>
				<text filter={`url(#${INK_ID})`}>
					<textPath
						href={`#${ARC_ID}`}
						startOffset="0"
						textLength={CIRCUMFERENCE}
						lengthAdjust="spacing"
					>
						{ring}
					</textPath>
				</text>
			</svg>

			<span className={style.play}>
				<Icon icon="fa-play" iconType="fa-solid" />
			</span>
		</div>
	);
}

export default CourseCursor;
